import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Link, useNavigate} from 'react-router-dom';
import {Turnstile} from '@marsidev/react-turnstile';
import contactService from '../../common/services/contactService';
import {handleError} from '../../common/utils/errorHandling';
import {getRuntimeConfig} from "../../common/services/runtimeConfig";
import {
  CenteredButton,
  CenteredDropdown,
  CenteredForm,
  CenteredHeader,
  CenteredInfo,
  CenteredInputText,
  CenteredLayout,
  CenteredTextarea
} from '../../layout/centered/components';
import InlineErrorMessage from '../../common/components/InlineErrorMessage';

const ContactInner: React.FC<{ onSuccess: (token: string) => void }> = ({onSuccess}) => {
  const {t} = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    title: 'other',
    content: ''
  });
  const [error, setError] = useState('');
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const navigate = useNavigate();

  const titleOptions = [
    {value: 'photo_order', label: t('common.pages.contact.titleOptions.photoOrder')},
    {value: 'other', label: t('common.pages.contact.titleOptions.other')}
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {name, value} = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError('');

    if (!captchaToken) {
      setError(t('common.pages.contact.captchaError'));
      return;
    }

    await handleError(
      async () => {
        const selectedTitle = titleOptions.find(opt => opt.value === formData.title)?.label || formData.title;

        await contactService.submitContactForm({
          name: formData.name,
          email: formData.email,
          title: selectedTitle,
          content: formData.content,
          captchaToken
        });
        navigate('/contact-confirmation');
      },
      'Contact form submission failed:',
      () => setError(t('common.pages.contact.errorFailed'))
    );
  };

  return (
    <CenteredLayout sectionId="contact-section">
      <CenteredHeader
        title={t('common.pages.contact.title')}
        subtitle={t('common.pages.contact.subtitle')}
      />

      <CenteredForm onSubmit={handleSubmit}>
        <CenteredInputText
          id="name"
          name="name"
          type="text"
          value={formData.name}
          onChange={handleInputChange}
          placeholder={t('common.pages.contact.namePlaceholder')}
          label={t('common.pages.contact.name')}
          icon="fa fa-user"
          required
        />

        <CenteredInputText
          id="email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleInputChange}
          placeholder={t('common.pages.contact.emailPlaceholder')}
          label={t('common.pages.contact.email')}
          icon="fa fa-envelope"
          required
        />

        <CenteredDropdown
          id="title"
          name="title"
          value={formData.title}
          onChange={handleInputChange}
          options={titleOptions}
          placeholder={t('common.pages.contact.titlePlaceholder')}
          label={t('common.pages.contact.topicLabel')}
          required
        />

        <CenteredTextarea
          id="content"
          name="content"
          value={formData.content}
          onChange={handleInputChange}
          placeholder={t('common.pages.contact.contentPlaceholder')}
          label={t('common.pages.contact.content')}
          rows={6}
          required
        />

        <InlineErrorMessage message={error}/>

        <Turnstile
          siteKey={getRuntimeConfig().TURNSTILE_SITE_KEY}
          options={{
            size: 'invisible',
            action: 'contact',
          }}
          onSuccess={(token) => {
            setCaptchaToken(token);
            onSuccess(token);
          }}
        />

        <CenteredButton
          type="submit"
          label={t('common.pages.contact.submit')}
          icon="fa fa-paper-plane"
          iconPos="left"
        />
      </CenteredForm>

      <CenteredInfo className="mt-8">
        <Link
          className="text-gray-800 hover:underline cursor-pointer font-medium"
          to={'/'}
        >
          {t('common.pages.contact.backToHome')}
        </Link>
      </CenteredInfo>
    </CenteredLayout>
  );
};

const Contact: React.FC = () => {
  return (
    <ContactInner onSuccess={() => {
    }}/>
  );
};

export default Contact;
